import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { AlertCircleIcon, Globe02Icon, KeyboardIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { LANGUAGE_MAP } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { TEXT_QUESTION_TYPES } from 'constants/formConstants';
import { getIgnoredFieldsFromLogicJumps, meetsLogic } from 'constants/logic';
import { getComponentProps as baseGetComponentProps, componentMap } from 'helpers/question-config';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { detectAllLanguages } from 'utils/answerTranslationService';
import { QuestionDataFactory } from 'utils/questionDataFactory';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { FormMessage, MessageType, Select } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import QuestionWrapper from '../Questions/QuestionWrapper';
import styles from './Form.module.scss';

// Stable empty array so MultiUpload doesn't get a new [] reference every render (avoids effect loop)
const EMPTY_UPLOADS = [];

const noHeaderComponents = [
  FieldTypes.Checkbox,
  FieldTypes.Consent,
  FieldTypes.Download,
  FieldTypes.Statement,
  FieldTypes.ToggleSwitch,
];

export const Form = forwardRef(
  (
    {
      candidateId,
      country = 'NZ',
      form,
      brand,
      includeCandidateReferenceValidation = false,
      candidateEmail,
      emailCompareErrorMessage = '',
      candidateContact,
      contactCompareErrorMessage = '',
      buttonTitle = 'Save',
      cancelAction = null,
      cancelActionTitle = 'Cancel',
      textParams = {},
      locale = 'en_GB',
      rules = [],
      footerComponent = null,
      onSubmit = () => {},
      getCandidateUpload = () => {},
      deleteCandidateUpload = () => {},
      signCandidateUpload = () => {},
      createCandidateUpload = () => {},
      allowTranslation = false,
      selectedLanguageCode = null,
      isTranslating = false,
      onLanguageChange = null,
      translatedFields = {},
      onProgressChange = null,
      onValuesChange = null,
      header = false,
      headerLogo = null,
      headerIcon = null,
      headerIconSize = 'xxlarge',
      headerIconBg = null,
      headerTitle = '',
      headerTitleColor = null,
      headerSubtitle = '',
      headerDescription = '',
      headerBadges = [],
      translationTitle = null,
      fieldRequiredVariantStyle = 'pill',
      detectionEndpoint = null,
      className = '',
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const errorRef = useRef(null);

    const [showError, setShowError] = useState(false);
    const [blockMessage, setBlockMessage] = useState(null);
    const [logicIds, setLogicIds] = useState([]);
    const [ignoredFields, setIgnoredFields] = useState([]);
    const [isDetecting, setIsDetecting] = useState(false);

    const {
      register,
      handleSubmit,
      watch,
      reset,
      getValues,
      setValue,
      trigger,
      formState: { errors },
      control,
    } = useForm({
      shouldFocusError: false,
      shouldUnregister: true,
      resetOptions: {
        keepErrors: true,
        keepValues: false,
        keepDirtyValues: false,
      },
    });

    // Expose functions
    useImperativeHandle(ref, () => ({
      reset,
    }));

    // Watch the controlling question (logic.field) whose answer is evaluated for each jump.
    // When logic.field and logic.from differ (e.g. "if Q1 is X, jump from Q4 to Q6"), we must
    // watch logic.field so logic re-runs when the controlling answer changes — not logic.from.
    useEffect(() => {
      if (!form) return;

      const logicOrders = (form.logic_jumps || [])
        .filter(l => l.operator !== 'always' && l.field != null)
        .map(l => l.field);
      const fieldsWithLogic = (form.fields || []).filter(field =>
        logicOrders.includes(field.order),
      );
      setLogicIds(fieldsWithLogic.map(f => `${f.id}`));
    }, [form]);

    // Hide fields by logic
    useEffect(() => {
      const maxOrder = form?.fields?.length > 0 ? Math.max(...form.fields.map(f => f.order)) : null;
      const ignoredFields = getIgnoredFieldsFromLogicJumps(
        form?.logic_jumps,
        form?.fields,
        watch,
        maxOrder,
      );

      setIgnoredFields(ignoredFields);
      for (const ignoreField of ignoredFields) {
        const field = form.fields.find(f => f.order === ignoreField);
        if (field) control.unregister(`${field.id}`);
      }
    }, [JSON.stringify(watch(logicIds)), form]);

    useEffect(() => {
      if (!onProgressChange || !form?.fields) return;
      const visibleFields = form.fields.filter(f => !ignoredFields.includes(f.order));
      const allValues = watch();
      const filled = visibleFields.filter(f => {
        const val = allValues[`${f.id}`];
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val)) return val.length > 0;
        return true;
      }).length;
      onProgressChange(
        visibleFields.length > 0 ? Math.round((filled / visibleFields.length) * 100) : 0,
      );
    }, [JSON.stringify(watch()), ignoredFields.length, form?.fields?.length, onProgressChange]);

    useEffect(() => {
      if (!onValuesChange) return;
      onValuesChange(watch());
    }, [JSON.stringify(watch()), onValuesChange]);

    const onError = () => {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setShowError(true);
    };

    const handleOnSubmit = async data => {
      // Check block rules before submitting
      const blockRules = (rules || []).filter(r => r.rule_type === 'block' && r.active);
      for (const rule of blockRules) {
        const field = form?.fields?.find(f => Number(f.order) === Number(rule.field));
        if (!field) continue;
        const value = data[field.id.toString()];
        if (meetsLogic(rule, value)) {
          setBlockMessage(rule.message || 'You cannot proceed based on your answer.');
          return;
        }
      }
      setBlockMessage(null);

      let enrichedData = data;

      if (allowTranslation && detectionEndpoint) {
        const textAnswers = {};
        for (const field of form?.fields || []) {
          if (!TEXT_QUESTION_TYPES.includes(field.type)) continue;
          const val = data[`${field.id}`];
          if (val && typeof val === 'string') {
            textAnswers[`${field.id}`] = val;
          }
        }

        if (Object.keys(textAnswers).length > 0) {
          setIsDetecting(true);
          const langResults = await detectAllLanguages(textAnswers, detectionEndpoint);
          setIsDetecting(false);

          enrichedData = { ...data };
          for (const [fieldId, langResult] of Object.entries(langResults)) {
            enrichedData[fieldId] = QuestionDataFactory.create(data[fieldId], {
              languageDetection: true,
              languageCode: langResult?.languageCode ?? null,
              score: langResult?.score ?? null,
            });
          }
        }
      }

      const dataWithLabels = Object.keys(data).reduce((acc, fieldId) => {
        const field = form?.fields?.find(f => `${f.id}` === fieldId);
        acc[fieldId] = {
          value: data[fieldId],
          ...field,
        };
        return acc;
      }, {});
      onSubmit(enrichedData, dataWithLabels);
    };

    return (
      <div className={cn('flex flex-col gap-8', className)}>
        <form className="w-full flex flex-col gap-8">
          <div ref={errorRef} />
          {header && (
            <div className=" flex flex-col gap-8">
              {headerLogo && (
                <img src={headerLogo} className="h-7 mb-2 object-contain object-left" alt="" />
              )}
              {(headerIcon || headerSubtitle || headerTitle) && (
                <div className="flex flex-col gap-4">
                  {headerIcon && <IconComponent icon={headerIcon} size={headerIconSize} />}
                  <div className="d-flex flex-column align-items-start u-gap-small">
                    {headerSubtitle && (
                      <span className="text-text-secondary text-xl">{headerSubtitle}</span>
                    )}
                    <h1 className="primary-text u-font-weight-500 u-margin-0 text-5xl">
                      {headerTitle}
                    </h1>
                    {headerDescription && (
                      <p
                        className="secondary-text u-font-size-medium font-light white-space-pre-line"
                        dangerouslySetInnerHTML={{ __html: headerDescription }}
                      />
                    )}
                  </div>
                </div>
              )}
              {headerBadges.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {headerBadges.map((badge, i) => (
                    <StatusPill
                      key={i}
                      icon={badge.icon}
                      iconSize="13"
                      text={badge.label}
                      status="neutral"
                      small
                      className="px-4 py-2 font-regular"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {allowTranslation && onLanguageChange && (
            <div className="flex flex-col gap-1 pb-8 mb-2 border-b border-neutral-4">
              <span className="text-text-primary text-xl font-medium flex items-center">
                <HugeiconsIcon icon={Globe02Icon} size={16} className="shrink-0 mr-2" />
                {translationTitle ?? t('components.form.translateTheFormInto')}
              </span>
              <div className="mt-2 w-full sm:w-1/2">
                {isTranslating ? (
                  <span className="text-text-tertiary text-xs">
                    {t('components.form.translating')}
                  </span>
                ) : (
                  <Select
                    name="form_language"
                    placeholder={t('components.form.selectLanguagePlaceholder')}
                    value={selectedLanguageCode}
                    inputProps={{
                      onChange: e => onLanguageChange(e.target.value || null),
                    }}
                    useSearch>
                    {Object.entries(LANGUAGE_MAP).map(([code, label]) => (
                      <Select.Item key={code} value={code}>
                        {label}
                      </Select.Item>
                    ))}
                  </Select>
                )}
              </div>
              <span className="text-text-tertiary text-lg font-regular flex items-start mt-2">
                <HugeiconsIcon icon={KeyboardIcon} size={14} className="shrink-0 mr-2" />
                To type in your own language, add that language's keyboard in your device settings,
                then switch to it when answering.
              </span>
            </div>
          )}
          {(showError || form?.message) && (
            <div className="flex flex-col gap-4 mb-16">
              {showError && (
                <FormMessage
                  type={MessageType.Error}
                  message="There are some invalid values. Please check the fields marked in red and re-submit."
                  onClose={() => setShowError(false)}
                />
              )}
              {form?.message && (
                <FormMessage
                  message={form?.message}
                  type={MessageType.Info}
                  closeable={false}
                  noMargin
                />
              )}
            </div>
          )}
          {isTranslating && allowTranslation && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-neutral-4 border-t-transparent animate-spin" />
              <span className="text-text-secondary text-sm">
                {t('components.form.translating')}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-16">
            {!isTranslating &&
              (form?.fields || [])
                ?.filter(field => !ignoredFields.includes(field.order))
                ?.map((field, index) => {
                  const tr = translatedFields[field.id] ?? null;
                  const handlers = {
                    getCandidateUpload,
                    signCandidateUpload,
                    createCandidateUpload,
                    deleteCandidateUpload,
                  };
                  const getFieldComponentProps = (questionOrValue, value) => {
                    const isQuestionArg =
                      questionOrValue &&
                      typeof questionOrValue === 'object' &&
                      !Array.isArray(questionOrValue) &&
                      questionOrValue.id != null;
                    const tr = translatedFields[field.id] ?? null;
                    const translatedOpts =
                      tr?.translated_options ?? field.translated_options ?? null;
                    const resolvedField = translatedOpts
                      ? { ...field, options: translatedOpts }
                      : field;
                    const q = isQuestionArg ? questionOrValue : resolvedField;
                    const v = isQuestionArg ? value : questionOrValue;
                    const props = baseGetComponentProps({
                      question: q,
                      value: v,
                      candidateId,
                      handlers,
                      brand,
                      noMargin: true,
                    });
                    if (q.type === FieldTypes.PhoneNumber) {
                      return {
                        ...props,
                        defaultCountry: country,
                        ...(includeCandidateReferenceValidation && {
                          compareContacts: candidateContact,
                          compareContactErrorMessage: contactCompareErrorMessage,
                        }),
                      };
                    }
                    if (q.type === FieldTypes.Email && includeCandidateReferenceValidation) {
                      return {
                        ...props,
                        compareEmailValidation: true,
                        compareEmail: candidateEmail,
                        compareErrorMessage: emailCompareErrorMessage,
                      };
                    }
                    return props;
                  };
                  return (
                    <div key={field.id} className={styles.container}>
                      <QuestionWrapper
                        gap="small"
                        titleSize="large"
                        question={field}
                        questionIndex={index}
                        control={control}
                        register={register}
                        getValues={getValues}
                        setValue={setValue}
                        trigger={trigger}
                        error={errors?.[`${field.id}`]}
                        componentMap={componentMap}
                        getComponentProps={getFieldComponentProps}
                        textParams={textParams}
                        locale={locale}
                        brand={brand}
                        translatedText={tr?.text ?? field.translated_text ?? null}
                        translatedDescription={
                          tr?.translated_description ?? field.translated_description ?? null
                        }
                        translatedOptions={
                          tr?.translated_options ?? field.translated_options ?? null
                        }
                        allowTranslation={allowTranslation}
                        showLanguageSelector={false}
                        selectedLanguageCode={selectedLanguageCode}
                        onLanguageChange={onLanguageChange}
                        isTranslating={isTranslating}
                        noMargin={true}
                        pillSize="small"
                        requirementVariantStyle={fieldRequiredVariantStyle}
                      />
                    </div>
                  );
                })}
          </div>
          {!isTranslating && (
            <>
              {footerComponent && footerComponent}
              <div
                className={cn('flex items-center gap-2 justify-between', {
                  'w-full': !cancelAction,
                })}>
                {cancelAction && (
                  <CustomButton
                    type="secondary"
                    small
                    brand={brand}
                    onClick={cancelAction}
                    className="max-w-max"
                    dataTestId="form-cancel-button">
                    {cancelActionTitle}
                  </CustomButton>
                )}
                <CustomButton
                  small
                  brand={brand}
                  disabled={isDetecting}
                  className={cancelAction ? 'max-w-max' : 'w-full'}
                  onClick={handleSubmit(handleOnSubmit, onError)}
                  dataTestId="form-submit-button">
                  {buttonTitle}
                </CustomButton>
              </div>
            </>
          )}
        </form>
        {blockMessage && (
          <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center pb-6 px-4">
            <div className="bg-red-1 border border-red-2 rounded-2xl px-6 py-4 flex items-start gap-3 max-w-lg w-full shadow-lg">
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={20}
                className="text-red-2 shrink-0 mt-0.5"
              />
              <p className="text-neutral-7 text-xl">{blockMessage}</p>
            </div>
          </div>
        )}
      </div>
    );
  },
);
